/**
 * editMeetingModal.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import React from "react";
import { useNavigate } from "react-router-dom";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
//import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
//import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
//import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { AuthContext } from "../auth/auth.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Membership } from "../models/auth";
import { Meeting, MeetingType } from "../models/meeting";
import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { Brand } from "../models/brand";


interface Props {
  open: boolean,
  meeting: Meeting | null,
  onClose(): void,
  onCancel?: () => void,
  didChangeMeeting(meeting: Meeting): void
}

export function EditMeetingDialog(props: Props): React.ReactElement<any, any> {
  const conferenceService = conferenceServiceFactory();
  const authContext = React.useContext<AuthContext>(AuthContext);
  const navigate = useNavigate();
  const [errMsg, setErrMsg] = React.useState<string>("");

  const [meetingName, setMeetingName] = React.useState<string>("");
  const [meetingType, setMeetingType] = React.useState<MeetingType>(MeetingType.roundtable);
  const [autoAccept, setAutoAccept] = React.useState<boolean>(false);
  const [enableSIP, setEnableSIP] = React.useState<boolean>(false);

  const [showCopyContent, setShowCopyContent] = React.useState<boolean>(false);
  const [meetingUrl, setMeetingUrl] = React.useState<string>("");

  const [copyLinkText, setCopyLinkText] = React.useState<string>("Copy To Clipboard");
  const [copyDescText, setCopyDescText] = React.useState<string>("Copy To Clipboard");

  const [memberSearchVal, setMemberSearchVal] = React.useState<string>("");
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  const [selectedMembers, setSelectedMembers] = React.useState<Array<Membership>>([]);
  const [searchtimer, setSearchTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [saveNameTimer, setSaveNameTimer] = React.useState<NodeJS.Timeout | null>(null);

  const editMode = props.meeting !== null;

  React.useEffect(function () {
    if (props.open) {
      setCopyLinkText("Copy To Clipboard");
      setCopyDescText("Copy To Clipboard");
      setShowCopyContent(false);
      setErrMsg("");
      if (props.meeting) {
        setMeetingName(props.meeting.meetingname);
        setMeetingUrl(conferenceService.urlForMeeting(props.meeting, authContext.domain));
        setAutoAccept(props.meeting.autoAccept);
        setEnableSIP(props.meeting.sipEnabled);
        setSelectedMembers([]);
        const authService = authServiceFactory();
        authService.membersFor(new Set(props.meeting.hostUserIDs)).then(memDict => {
          if (!props.meeting) { return; } // make TS happy
          const members: Array<Membership> = [];
          for (const hostID of props.meeting.hostUserIDs) {
            const m = memDict[hostID];
            if (m) { members.push(m); }
          }
          setSelectedMembers(members);
        }, err => {
          console.error("Error fetching members:", err);
        });
      } else {
        const meetingName = conferenceService.generateName();
        setMeetingName(meetingName);
        setMeetingUrl("");
        const membership = authServiceFactory().currentAccountMembership;
        if (membership) {
          setSelectedMembers([membership]);
        } else {
          setSelectedMembers([]);
        }
      }
    }
  }, [props.open]);

  async function createMeetingNow() {
    try {
      const meeting = await createMeeting();
      if (!meeting) { return; }
      navigate(`/meeting/${meeting._id}/${meeting.token}`);
    } catch (err) {
      console.error("Error creating meeting:", err);
    }
  }

  async function createMeetingLater() {
    try {
      const meeting = await createMeeting();
      if (!meeting) { return; }
      setShowCopyContent(true);
      setMeetingUrl(conferenceService.urlForMeeting(meeting, authContext.domain));
      props.didChangeMeeting(meeting);
    } catch (err) {
      console.error("Error creating meeting:", err);
    }
  }

  async function createMeeting(): Promise<Meeting | null> {
    if (!authContext.membership) { return null; }
    const accountID = authContext.membership.accountID;
    //const groupService = groupServiceFactory();

    const meeting = await conferenceService.createMeeting(accountID, {
      meetingname: meetingName,
      meetingtype: meetingType,
      autoAccept: autoAccept,
      sipEnabled: enableSIP,
      hostUserIDs: selectedMembers.length > 0 ? selectedMembers.map(m => m.userID) : undefined
    });

    /*const group: Group = {
      _id: meeting._id,
      accountID: accountID,
      groupname: meetingName,
      desc: "",
      grouptype: GroupType.meeting,
      epoch: 0,
      msgCounter: 0,
      archiving: false,
      protoVersion: PROTOCOL_VERSION
    };

    await groupService.createGroup(accountID, group);*/

    return meeting;
  }

  function updateMeetingName(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    setMeetingName(inputElem.value);
    // editMode debounces this input below
  }
  function updateMeetingType(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    setMeetingType(inputElem.value as MeetingType);
    // cannot currently change in editMode
  }
  function changedAutoAccept(event: React.ChangeEvent<HTMLInputElement>) {
    setAutoAccept(event.target.checked);
    if (editMode) {
      updateMeeting({ autoAccept: event.target.checked });
    }
  }
  function changedSIP(event: React.ChangeEvent<HTMLInputElement>) {
    setEnableSIP(event.target.checked);
    if (editMode) {
      updateMeeting({ sipEnabled: event.target.checked });
    }
  }
  function renewToken() {
    updateMeeting({}, true);
  }
  function renewSipCode() {
    updateMeeting({}, false, true);
  }

  async function updateMeeting(parts: Partial<Meeting>, newToken = false, newCode = false): Promise<Meeting | null> {
    try {
      if (!authContext.membership || !props.meeting) { return null; }
      const accountID = authContext.membership.accountID;
      const meeting = await conferenceService.updateMeeting(
        accountID, props.meeting._id, parts, newToken, newCode);
      props.didChangeMeeting(meeting);
      return meeting;
    } catch (err) {
      console.error("Error updating meeting:", err);
      setErrMsg((err as Error).message);
    }
    return null;
  }

  React.useEffect(function () {
    if (!editMode) { return; }
    if (saveNameTimer) { clearTimeout(saveNameTimer); }
    setSaveNameTimer(setTimeout(function () {
      updateMeeting({ meetingname: meetingName });
    }, 500));
  }, [meetingName]);

  async function copyLinkToClipboard() {
    try {
      await navigator.clipboard.writeText(meetingUrl);
      setCopyLinkText("Copied!")
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      setErrMsg("Couldn't copy to clipboard");
    }
  }

  function findGroupMembers(searchVal: string) {
    const authService = authServiceFactory();
    authService.getAccountMembers(searchVal, 100).then(mems => {
      mems.sort((a, b): number => {
        const res = a.lastname.localeCompare(b.lastname);
        if (res != 0) { return res; }
        return a.firstname.localeCompare(b.firstname);
      });
      setMembers(mems);
    }, err => {
      console.error("Error fetching members:", err);
    });
  }
  React.useEffect(function () {
    if (searchtimer) { clearTimeout(searchtimer); }
    setSearchTimer(setTimeout(function () {
      findGroupMembers(memberSearchVal);
    }, 500));
  }, [memberSearchVal]);

  function focusedSearch() {
    findGroupMembers(memberSearchVal);
  }

  async function selectedOption(_event: React.SyntheticEvent, values: readonly Membership[] | null) {
    if (!values) { return; }
    const memMap: { [id: string]: Membership } = {};
    for (const val of values) {
      memMap[val.userID] = val;
    }
    const members = Object.values(memMap);
    setSelectedMembers(members);
    if (editMode && members.length > 0) {
      updateMeeting({ hostUserIDs: members.map(m => m.userID) });
    }
  }

  const productName = authContext.brand === Brand.GLYPH ? "GLYPH" : "Mercury Workspace";
  let googleLink = "";
  let description = "";
  let icsData = "";
  if (props.meeting && showCopyContent) {
    description = conferenceService.textDescForMeeting(props.meeting, authContext.domain, productName, meetingUrl);
    googleLink = conferenceService.googleLinkForMeeting(props.meeting, authContext.domain, productName, meetingUrl);
    icsData = conferenceService.iCalFormatted(props.meeting, authContext.domain, productName, meetingUrl, description);
  }

  async function copyDesc() {
    try {
      await navigator.clipboard.writeText(description);
      setCopyDescText("Copied!")
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      setErrMsg("Couldn't copy to clipboard");
    }
  }

  // If the user clicks outside the dialog, we don't want to close it
  function onCloseDialog(event: React.SyntheticEvent, reason: string) {
    void event;
    if (reason && reason === "backdropClick") {
      return;
    }
    props.onClose();
  }
  return <Dialog className="new-meeting-modal"
    open={props.open} onClose={onCloseDialog}>
    <DialogTitle>{editMode ? "Edit Meeting" : "Create Meeting"}</DialogTitle>
    <DialogContent>
      <div className="form-wrapper">
        {showCopyContent ? <div className="meeting-info-container">
          <div className="form-row-narrow">
            <FormControl fullWidth>
              <OutlinedInput id="link-display" size="small" type="email" name="meetinglink"
                color="secondary"
                value={meetingUrl} fullWidth
                endAdornment={<InputAdornment position="end">
                  <IconButton onClick={copyLinkToClipboard}>
                    <Tooltip title={copyLinkText}>
                      <i className="fa-solid fa-copy" />
                    </Tooltip>
                  </IconButton>
                </InputAdornment>} />
            </FormControl>
          </div>
          <div className="form-row">
            <FormControl fullWidth>
              <TextField id="link-display" size="small" type="email" name="meetinglink"
                color="secondary"
                rows={9}
                value={description} fullWidth multiline />
            </FormControl>
          </div>
          <div className="button-row">
            <Button href={googleLink} target="_blank" rel="nofollow"
              variant="contained" color="secondary">
              <Tooltip title="Add to Google Calendar">
                <i className="fa-brands fa-google" />
              </Tooltip>
            </Button>
            <Button href={icsData} target="_blank" rel="nofollow"
              download={`${meetingName}.ics`}
              variant="contained" color="secondary">
              <Tooltip title="Add to Calendar">
                <i className="fa-solid fa-calendar" />
              </Tooltip>
            </Button>
            <Button color="primary" variant="contained"
              disableRipple disableTouchRipple
              onClick={copyDesc}>
              {copyDescText}
            </Button>
          </div>
        </div> :
          <>
            <div className="form-row">
              <TextField id="meetingname" name="meetingName" fullWidth required
                label="Meeting Name" size="small"
                variant="outlined" color="secondary"
                value={meetingName} onChange={updateMeetingName} />
            </div>
            <div className="form-row">
              <Autocomplete id="account-member-search"
                multiple
                fullWidth
                size="small"
                options={members}
                getOptionLabel={(member: Membership) => `${member.firstname} ${member.lastname}`}
                isOptionEqualToValue={(o, v) => o.userID === v.userID}
                value={selectedMembers}
                onChange={selectedOption}
                onFocus={focusedSearch}
                inputValue={memberSearchVal}
                onInputChange={(_event, newInputValue) => {
                  setMemberSearchVal(newInputValue);
                }}
                renderInput={(params) =>
                  <TextField variant="outlined" color="secondary" {...params} placeholder="Hosts:" />} />
            </div>
            {editMode ? <>
              <div className="form-row">
                <FormLabel component="legend">Meeting Secrets</FormLabel>
                <div className="meeting-secret">
                  <div><span className="label">URL token: </span>{props.meeting?.token || ""}</div>
                  <IconButton size="small" disableFocusRipple disableRipple disableTouchRipple
                    onClick={() => renewToken()}>
                    <i className="fa-solid fa-rotate" />
                  </IconButton>
                </div>
                {enableSIP && <div className="meeting-secret">
                  <div><span className="label">PIN: </span>{conferenceService.displayCode(props.meeting?.sipCode || "")}</div>
                  <IconButton size="small" disableFocusRipple disableRipple disableTouchRipple
                    onClick={() => renewSipCode()}>
                    <i className="fa-solid fa-rotate" />
                  </IconButton>
                </div>}
              </div>
            </> : <div className="form-row">
              <FormControl component="fieldset" className="meeting-type-selection" fullWidth>
                <FormLabel component="legend">Meeting Type</FormLabel>
                <RadioGroup name="meetingtype" color="secondary"
                  value={meetingType}
                  onChange={updateMeetingType}>
                  <FormLabel>
                    <Radio value={MeetingType.roundtable} name="meetingtype" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Round Table</dt>
                      <dd>All participants can publish audio and video.</dd>
                    </div>
                  </FormLabel>
                  <FormLabel>
                    <Radio value={MeetingType.webinar} name="meetingtype" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Broadcast</dt>
                      <dd>Only hosts can publish audio and video.</dd>
                    </div>
                  </FormLabel>
                </RadioGroup>
              </FormControl>
            </div>}
            <div>
              <FormLabel component="legend">Other Settings</FormLabel>
            </div>
            <div className="form-row">
              <FormControl component="fieldset" fullWidth>
                <FormLabel>
                  <Checkbox checked={autoAccept} onChange={changedAutoAccept} />
                  <div className="radio-label-desc">
                    <dt>Bypass Waiting Room</dt>
                    <dd>Participants enter the meeting immediately.</dd>
                  </div>
                </FormLabel>
                <FormLabel>
                  <Checkbox checked={enableSIP} onChange={changedSIP} />
                  <div className="radio-label-desc">
                    <dt>Enable Telephony</dt>
                    <dd>Allow participates to call into this meeting using
                      VoIP or the public telephone network. Take caution
                      enabling this. We're both unable to authenticate callers
                      and unable to encrypt communication over the public
                      telephone network!</dd>
                  </div>
                </FormLabel>
              </FormControl>
            </div>
          </>}
      </div>
      <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
    </DialogContent>
    <DialogActions>
      {showCopyContent ? <>
        <Button variant="contained" onClick={props.onClose}>Close</Button>
      </> :
        editMode ? <>
          <Button disableRipple disableTouchRipple variant="contained"
            color="secondary"
            onClick={() => setShowCopyContent(true)}>Next</Button>
          <Button disableRipple disableTouchRipple variant="contained"
            color="primary"
            onClick={props.onClose}>
            Done
          </Button>
        </> : <>
          <Button disableRipple disableTouchRipple
            color="secondary"
            onClick={props.onCancel || props.onClose}>
            Cancel
          </Button>
          <Button disableRipple disableTouchRipple variant="contained"
            color="secondary"
            onClick={createMeetingLater}>Save for Later</Button>
          <Button disableRipple disableTouchRipple variant="contained"
            color="primary"
            onClick={createMeetingNow}>
            Start
          </Button>
        </>}

    </DialogActions>
  </Dialog>;
}
